import type { ReactFlowCanvasEdge } from '../../../../hooks/useYCanvas'
import type { VoiceProfileDict, VoiceProfileDictYMap } from '../../../../generated/nodeDataTypes'

type VoiceProfileEdge = Pick<ReactFlowCanvasEdge, 'target' | 'targetHandle'>
type VoiceProfileLike = VoiceProfileDict | VoiceProfileDictYMap | undefined
type VoiceProfileStringKey =
  | 'provider'
  | 'voice_id'
  | 'voice_name'
  | 'description'
  | 'voice_prompt'
  | 'name'
  | 'audio_sample'
  | 'speech_text'
  | 'pronunciation_guide'
  | 'voice_source'
  | 'voice_ref'
  | 'language'
  | 'locale'
  | 'use_case'
type VoiceProfileArrayKey = 'characteristics' | 'descriptors'

const VOICE_PROFILE_STRING_KEYS: VoiceProfileStringKey[] = [
  'provider',
  'voice_id',
  'voice_name',
  'description',
  'voice_prompt',
  'name',
  'audio_sample',
  'speech_text',
  'pronunciation_guide',
  'voice_source',
  'voice_ref',
  'language',
  'locale',
  'use_case',
]

const PROVIDER_NEUTRAL_STRING_KEYS: VoiceProfileStringKey[] = [
  'description',
  'voice_prompt',
  'name',
  'pronunciation_guide',
  'language',
  'locale',
  'use_case',
]

const PROVIDER_NEUTRAL_ARRAY_KEYS: VoiceProfileArrayKey[] = ['characteristics', 'descriptors']
const GENERATED_SAMPLE_BRIEF_KEYS: VoiceProfileStringKey[] = ['description', 'voice_prompt', 'name']

function cleanString(value: string | undefined): string | undefined {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

export function plainVoiceProfile(profile: VoiceProfileLike): VoiceProfileDict | undefined {
  if (!profile) return undefined
  const plain: VoiceProfileDict = {}
  const assignString = (key: VoiceProfileStringKey) => {
    const value = cleanString(profile[key])
    if (value) plain[key] = value
  }
  VOICE_PROFILE_STRING_KEYS.forEach(assignString)
  if (profile.characteristics?.length) plain.characteristics = [...profile.characteristics]
  if (profile.descriptors?.length) plain.descriptors = [...profile.descriptors]
  return Object.keys(plain).length > 0 ? plain : undefined
}

export function providerNeutralVoiceProfile(profile: VoiceProfileLike): VoiceProfileDict | undefined {
  const plain = plainVoiceProfile(profile)
  if (!plain) return undefined
  const neutral: VoiceProfileDict = {}
  for (const key of PROVIDER_NEUTRAL_STRING_KEYS) {
    const value = plain[key]
    if (cleanString(value)) {
      neutral[key] = value
    }
  }
  for (const key of PROVIDER_NEUTRAL_ARRAY_KEYS) {
    const value = plain[key]
    if (value?.length) neutral[key] = [...value]
  }
  return Object.keys(neutral).length > 0 ? neutral : undefined
}

export function hasVoiceBriefForGeneratedSample(profile: VoiceProfileLike): boolean {
  const plain = plainVoiceProfile(profile)
  if (!plain) return false
  return GENERATED_SAMPLE_BRIEF_KEYS.some((key) => {
    const value = plain[key]
    return typeof value === 'string' && value.trim().length > 0
  })
}

export function hasIncomingVoiceProfileEdge(
  edges: readonly VoiceProfileEdge[],
  nodeId: string,
): boolean {
  return edges.some((edge) => edge.target === nodeId && edge.targetHandle === 'in_voice_profile')
}

export function shouldClearLegacyInputVoiceProfile(
  edges: readonly VoiceProfileEdge[],
  nodeId: string,
): boolean {
  return !hasIncomingVoiceProfileEdge(edges, nodeId)
}

export function buildRealizedVoiceProfile(
  neutralProfile: VoiceProfileDict | undefined,
  selectedProfile: VoiceProfileDict | undefined,
): VoiceProfileDict | undefined {
  if (!neutralProfile && !selectedProfile) return undefined

  const realized: VoiceProfileDict = neutralProfile ? { ...neutralProfile } : {}
  const provider = cleanString(selectedProfile?.provider)
  const voiceId = cleanString(selectedProfile?.voice_id)
  const voiceName = cleanString(selectedProfile?.voice_name) ?? cleanString(selectedProfile?.name)
  const voiceRef = cleanString(selectedProfile?.voice_ref)

  if (provider) realized.provider = provider
  if (voiceId) realized.voice_id = voiceId
  if (voiceName) realized.voice_name = voiceName
  if (voiceRef) {
    realized.voice_ref = voiceRef
    realized.voice_source = cleanString(selectedProfile?.voice_source) ?? 'elevenlabs_voice_library'
  } else if (cleanString(selectedProfile?.voice_source)) {
    realized.voice_source = cleanString(selectedProfile?.voice_source)
  }
  if (!cleanString(realized.name) && voiceName) {
    realized.name = voiceName
  }

  if (Array.isArray(neutralProfile?.characteristics)) {
    realized.characteristics = [...neutralProfile.characteristics]
  }
  if (Array.isArray(neutralProfile?.descriptors)) {
    realized.descriptors = [...neutralProfile.descriptors]
  }

  return Object.keys(realized).length > 0 ? realized : undefined
}
